/**
 * TransactionChargesV2 – V2 design system implementation
*/

import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import {
    PageBodyTitle,
    Card,
    Table,
    PageLoader,
    ListPageFilterBar,
    Tabs,
    ErrorMessage,
    SuccessMessage,
    StatusBadge,
} from "../../design-system";

import { CsvDown } from "../../integrations/export";
import { theme } from "../../design-system/theme";

import RequestV2 from "../../../api/RequestV2";
import Config from "../../../utils/config";

import { ToggleSwitch } from "../../design-system";

const PageWrapper = styled.div`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: ${theme.spacing.sm} 0 0;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: ${theme.spacing.lg};
  gap: ${theme.spacing.md};
`;

const FILTER_INITIAL = [
    { name: "entity", type: "text", value: "", placeholder: "Org" },
    { name: "provider", type: "text", value: "", placeholder: "Provider" },
    { name: "transactionType", type: "text", value: "", placeholder: "Transaction Type" },
    { name: "paymentMode", type: "text", value: "", placeholder: "Payment Mode" },
    {
        name: "status",
        type: "select",
        value: "",
        placeholder: "Status",
        options: [
            { value: "ACTIVE", label: "Active" },
            { value: "INACTIVE", label: "Inactive" }
        ],
        placeholderOnly: true
    }
];

export default function TransactionChargesV2() {

    const [controls, setControls] = useState(FILTER_INITIAL);
    const [list, setList] = useState([]);

    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    const [activeTab, setActiveTab] = useState("ORG");
    const [allData, setAllData] = useState([]);

    const [message, setMessage] = useState({ success: "", error: "" });
    const [dataSet, setDataSet] = useState("");

    const TABS = [
        { value: "ORG", label: "ORG" },
        { value: "BANK", label: "BANK" },
        { value: "PG", label: "PG" },
        { value: "ROLE", label: "ROLE" }
    ];

    /* ---------------- TABLE COLUMNS ---------------- */

    const columns = useMemo(() => {

        const base = [
            { key: "applicableValue", label: activeTab },
            { key: "provider", label: "Provider" },
            { key: "transactionType", label: "Transaction Type" },
            { key: "paymentMode", label: "Payment Mode" },
            { key: "minAmount", label: "Min Amount" },
        ];

        if (activeTab === "BANK" || activeTab === "PG") {
            base.push({ key: "maxAmount", label: "Max Amount" });
        }

        if (activeTab === "BANK" || activeTab === "ROLE") {
            base.push({ key: "priority", label: "Priority" });
        }

        base.push(
            { key: "chargeType", label: "Charge Type" },
            {
                key: "chargeValue",
                label: "Charge Value",
                render: (_, row) =>
                    row?.chargeType === "PERCENTAGE"
                        ? `${row?.chargeValue}%`
                        : `₹ ${row?.chargeValue ?? 0}`
            },
            {
                key: "gst",
                label: "GST",
                render: (_, row) =>
                    row?.gstApplicable
                        ? row?.gstType === "PERCENTAGE"
                            ? `${row?.gstValue}%`
                            : `₹ ${row?.gstValue ?? 0}`
                        : "No GST"
            },
            {
                key: "status",
                label: "Status",
                render: (_, row) => (row?.status ? <StatusBadge status={row.status} /> : "—"),
            },
            {
                key: "createdAt",
                label: "Created At",
                render: (_, row) =>
                    row?.createdAt ? row.createdAt.split("T")[0] : "-"
            },

            {
                key: "action",
                label: "Update Status",
                align: "center",
                render: (_v, row) => (
                    <ToggleSwitch
                        variant="success"
                        showTrackLabels
                        checked={row?.status === "ACTIVE"}
                        onChange={() => requestToggle(row)}
                        inputProps={{ "aria-label": `Toggle ${row?.provider}` }}
                    />
                )
            }
        );

        return base;

    }, [activeTab]);

    /* ---------------- FETCH LIST ---------------- */

    const fetchList = useCallback(() => {

        setLoading(true);
        setListError("");

        const payload = {
            type: activeTab,
            page: currentPage - 1,
            size: pageSize
        };

        const api = new RequestV2(
            "",
            (data) => {

                setLoading(false);

                const content = data?.metaInfo?.data ?? [];

                setAllData(content);
                setList(content);
                setTotalElements(data?.metaInfo?.totalItems ?? content.length);
            },
            (err) => {

                setLoading(false);

                setListError(
                    err?.response?.data?.message || "Failed to fetch transaction charges"
                );
            }
        );

        api.get(`${Config.apis.admin.FETCH_TRANSACTION_CHARGES}`, { params: payload });

    }, [activeTab, currentPage, pageSize]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    /* ---------------- FILTER ---------------- */

    const getFilteredData = () => {

        let filtered = [...allData];

        const getValue = (name) =>
            controls.find(c => c.name === name)?.value ?? "";

        const normalize = (v) =>
            (v || "").toString().toLowerCase().trim();

        const entity = normalize(getValue("entity"));
        const provider = normalize(getValue("provider"));
        const transactionType = normalize(getValue("transactionType"));
        const paymentMode = normalize(getValue("paymentMode"));
        const status = getValue("status");

        return filtered.filter(item =>
            (!entity || normalize(item?.applicableValue).includes(entity)) &&
            (!provider || normalize(item?.provider).includes(provider)) &&
            (!transactionType || normalize(item?.transactionType).includes(transactionType)) &&
            (!paymentMode || normalize(item?.paymentMode).includes(paymentMode)) &&
            (!status || item?.status === status)
        );
    };

    const handleFilterChange = (name, value) => {
        setControls(prev =>
            prev.map(c => (c.name === name ? { ...c, value } : c))
        );
    };

    const onSearch = () => {
        const filtered = getFilteredData();
        setList(filtered);
        setCurrentPage(1);
    };

    const onReset = () => {
        const reset = FILTER_INITIAL.map(filter => ({
            ...filter,
            value: ""
        }));

        setControls(reset);
        setList(allData);
        setCurrentPage(1);
    };

    const filterConfig = useMemo(() => {

        const labelMap = {
            ORG: "Org",
            BANK: "Bank",
            PG: "PG",
            ROLE: "Role"
        };

        return controls.map(control => {

            if (control.name === "entity") {
                return {
                    ...control,
                    placeholder: labelMap[activeTab]
                };
            }

            return control;

        });

    }, [controls, activeTab]);

    if (loading && list.length === 0 && !listError) {
        return <PageLoader />;
    }

    /* ---------------- UPDATE STATUS ---------------- */

    const requestToggle = (row) => {
        const newStatus = row.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

        setMessage({ success: "", error: "" });

        const api = new RequestV2(
            "",
            () => {
                setMessage({ success: "Status updated successfully" });
                setDataSet(Date.now().toString());
                fetchList();
            },
            (err) => {
                setMessage({
                    error: err?.response?.data?.message || "Failed to update status",
                });
                setDataSet(Date.now().toString());
            }
        );

        api.post(
            `${Config.apis.admin.UPDATE_STATUS_TRANSACTION_CHARGES}?uuid=${row.uuid}&status=${newStatus}`
        );
    };

    return (
        <PageWrapper>

            {message.success && (
                <SuccessMessage successMessage={message.success} dataset={dataSet} />
            )}

            {message.error && (
                <ErrorMessage error={message.error} datetimestamp={dataSet} />
            )}

            {listError && !message.error && <ErrorMessage error={listError} />}

            <TopBar>
                <PageBodyTitle>Transaction Charges</PageBodyTitle>

                <div style={{ marginLeft: "auto" }}>
                <CsvDown
                    title="CSV"
                    headers={columns.map(col => ({
                        label: col.label,
                        key: col.key
                    }))}
                    exportRows={list}
                    reportName="transaction-charges.csv"
                />
                </div>
            </TopBar>

            <Tabs
                options={TABS}
                value={activeTab}
                onChange={setActiveTab}
                variant="underline"
            />

            <Card>

                <ListPageFilterBar
                    filters={filterConfig}
                    onFilterChange={handleFilterChange}
                    onSearch={onSearch}
                    onReset={onReset}
                />

                <Table
                    data={list}
                    columns={columns}
                    rowKey={(row, idx) => row?.uuid ?? idx}
                    loading={loading}
                    emptyMessage="No records found"
                    pagination={{
                        currentPage,
                        pageSize,
                        total: totalElements,
                        onPageChange: setCurrentPage,
                        pageSizeOptions: [10, 20, 50],
                        onPageSizeChange: size => {
                            setPageSize(size);
                            setCurrentPage(1);
                        }
                    }}
                />

            </Card>

        </PageWrapper>
    );
}
